/**
 * @file Settings hooks — schema, current values, per-key history,
 *       and the write mutation.
 * @module @labcraft/admin/ui/hooks/use-settings
 *
 * `useSettingsSchema` and `useSettingsValues` fire one query each;
 * the panel composes the two via TanStack's structural rendering
 * (each becomes a separate `useQuery` so a slow `/values` doesn't
 * delay the schema's first paint). `useSettingHistory(key)` runs
 * lazily — the row component only mounts it when the operator
 * expands the history popover.
 *
 * `useSetSettingMutation` invalidates the entire `['admin','settings']`
 * prefix on success — the freshly-written value lands in `/values`
 * and the history grows by one row, so both need a refetch.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * / `useMutation` outside it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import type {
  SetSettingResponse,
  SettingsHistoryResponse,
  SettingsSchemaResponse,
  SettingsValuesResponse,
} from '../../shared/settings_routes.ts'
import {
  qkSettingHistory,
  qkSettingsSchema,
  qkSettingsValues,
} from './query_keys.ts'

/** Per-daemon registry of declared keys. Cached server-side. */
export function useSettingsSchema() {
  const client = useClient<Admin>()
  return useQuery<SettingsSchemaResponse>({
    queryKey: qkSettingsSchema,
    queryFn: () => client.settings.schema(),
  })
}

/** Current value of every declared key. */
export function useSettingsValues() {
  const client = useClient<Admin>()
  return useQuery<SettingsValuesResponse>({
    queryKey: qkSettingsValues,
    queryFn: () => client.settings.values(),
  })
}

/**
 * Full history of one daemon-scoped key, newest first. Lazy — pass
 * `enabled: false` (via the `enabled` arg) so the query doesn't fire
 * until the row's history popover opens.
 *
 * @param daemon - The owning daemon id (`account`, `lab`, `www`, …).
 * @param key - The settings key to read history for.
 * @param enabled - Whether the query should fire (default `true`).
 */
export function useSettingHistory(
  daemon: string,
  key: string,
  enabled: boolean = true,
) {
  const client = useClient<Admin>()
  return useQuery<SettingsHistoryResponse>({
    queryKey: qkSettingHistory(daemon, key),
    queryFn: () => client.settings.history(daemon, key),
    enabled,
  })
}

/**
 * Write a new value for one daemon-scoped key. Invalidates the entire
 * `['admin','settings']` prefix on success so `/values` and the
 * key's history refetch on the next render.
 */
export function useSetSettingMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<
    SetSettingResponse,
    Error,
    { daemon: string; key: string; value: unknown }
  >({
    mutationFn: ({ daemon, key, value }) =>
      client.settings.set(daemon, key, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] })
    },
  })
}
